// The editor's draft and what the server holds of it (spec docs/specs/editor/live-sync-web.md): the
// undo history around the one reducer (`history.ts`), the version the server holds as far as this
// tab knows (`held`), a newer save the draft could not take in (`conflict`), and this tab's saves
// that may yet prove written (`sent`). One pure reducer,
// so another save is merged into the draft, or met as a conflict, in the very step that reads the
// draft: a drag's move cannot come between the check and the merge (spec live-sync-merge.md,
// decision 4).
import { editHistory, historyOf, type History } from "./history";
import { refusal, type Timeline, type TimelineEdit } from "./timeline";
import { sameSaved } from "./wire";

/** What the server holds of the project: the version a save names, and its timeline and name. */
export interface Held {
  version: number;
  timeline: Timeline;
  name: string;
}

/** A save this tab sent: the version it named, and the timeline and name it sent. */
export interface Sent {
  from: number;
  timeline: Timeline;
  name: string;
}

/**
 * How many saves the draft keeps that may yet prove written. Past it, the newest takes the last
 * place: the first are the likeliest written, sent as the connection fell.
 */
export const SENT_KEPT = 10;

export interface Draft {
  history: History;
  held: Held;
  /** A newer save the draft could not take in: Saved elsewhere, until Keep mine or Take theirs. */
  conflict: Held | null;
  /**
   * This tab's saves that may yet prove written, oldest first, which a read may bring back: each
   * stays, answered or not, until the version held passes the one it named (decision 5).
   */
  sent: readonly Sent[];
}

export type DraftAction =
  | { type: "edit"; edit: TimelineEdit; at: number }
  | { type: "undo" }
  | { type: "redo" }
  /** The server's newer versions, oldest first, as a read of what changed gave them. */
  | { type: "sync"; versions: readonly Held[] }
  /** A save of this tab sent, naming `from`. */
  | { type: "sending"; sent: Sent }
  /** A save of this tab landed: the server holds it now. */
  | { type: "saved"; held: Held }
  /** Take theirs: the conflict's version as one undo step (the assistant's Revert's `restore`). */
  | { type: "takeTheirs"; at: number };

export function draftOf(held: Held): Draft {
  return { history: historyOf(held.timeline), held, conflict: null, sent: [] };
}

/**
 * Whether a version newer than the one held is this tab's own save, come back before its answer or
 * after a lost one: the one after the version a save named, holding what it sent. A save named the
 * version held, or Keep mine's the conflict's (decision 8).
 */
function ownSave(draft: Draft, next: Held): boolean {
  return (
    next.version > draft.held.version &&
    draft.sent.some((one) => next.version === one.from + 1 && one.name === next.name && sameSaved(one.timeline, next.timeline))
  );
}

/**
 * The draft holding `held`: a conflict no newer is settled, and a save that named an older version
 * goes, since it can no longer be written.
 */
function holding(draft: Draft, held: Held): Draft {
  const settled = draft.conflict !== null && draft.conflict.version <= held.version;
  const sent = draft.sent.filter((one) => one.from >= held.version);
  return { ...draft, held, conflict: settled ? null : draft.conflict, sent };
}

export function editDraft(draft: Draft, action: DraftAction): Draft {
  switch (action.type) {
    case "edit":
    case "undo":
    case "redo": {
      const history = editHistory(draft.history, action);
      return history === draft.history ? draft : { ...draft, history };
    }
    case "sync": {
      // This tab's own save, the newest among them, is held as it is, with the history untouched:
      // the versions before it are its own too, or those its Keep mine overwrote. Each after it is
      // another's, merged in turn from the one held before it. A project read whole gives one
      // version, maybe several ahead.
      const at = action.versions.findLastIndex((version) => ownSave(draft, version));
      const own = action.versions[at];
      let next = own === undefined ? draft : holding(draft, own);
      for (const version of action.versions.slice(at + 1)) {
        if (version.version <= next.held.version) continue;
        const sync = { type: "sync", base: next.held.timeline, theirs: version.timeline } as const;
        if (refusal(next.history.present, sync) !== null) return { ...next, conflict: action.versions.at(-1) ?? version };
        next = { ...holding(next, version), history: editHistory(next.history, sync) };
      }
      return next;
    }
    case "sending": {
      const sent = [...draft.sent, action.sent].filter((one) => one.from >= draft.held.version);
      return { ...draft, sent: sent.length <= SENT_KEPT ? sent : [...sent.slice(0, SENT_KEPT - 1), ...sent.slice(-1)] };
    }
    case "saved":
      // An answer older than the version held changes nothing.
      return action.held.version < draft.held.version ? draft : holding(draft, action.held);
    case "takeTheirs": {
      const theirs = draft.conflict;
      if (theirs === null) return draft;
      const restore = { type: "edit", edit: { type: "restore", timeline: theirs.timeline }, at: action.at } as const;
      return { ...holding(draft, theirs), history: editHistory(draft.history, restore) };
    }
    default:
      return action satisfies never;
  }
}
